import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Skeleton, Stack } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { DataGrid } from "@mui/x-data-grid";
import { apiGet } from "../../shared/apiClient";
import "./dashboard.css";

const CHART_GREEN = "#16a34a";
const CHART_GOLD = "#c9a227";
const TIER_COLORS = ["#14532d", "#16a34a", "#22c55e", "#4ade80", "#c9a227"];

const gridSx = {
  border: "none",
  fontFamily: "inherit",
  fontSize: "0.8125rem",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    fontWeight: 700,
    fontSize: "0.7rem",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "#64748b"
  },
  "& .MuiDataGrid-cell": { borderBottomColor: "#f1f5f9" },
  "& .MuiDataGrid-row:hover": { backgroundColor: "#f8fffb !important" }
};

function formatChange(current, previous) {
  if (previous === null || previous === undefined) return { text: "0.0%", up: true };
  if (previous === 0) return { text: current === 0 ? "0.0%" : "+100.0%", up: current >= 0 };
  const diff = ((current - previous) / previous) * 100;
  return { text: `${diff > 0 ? "+" : ""}${diff.toFixed(1)}%`, up: diff >= 0 };
}

function KpiIconProducts() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    </svg>
  );
}

function KpiIconOrders() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
      <path d="M3 6h18M16 10a4 4 0 0 1-8 0" />
    </svg>
  );
}

function KpiIconCustomers() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function KpiIconRevenue() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3h12M6 8h12M6 3l10 18M14 3c0 2.76-2.24 5-5 5M9 3c0 2.76 2.24 5 5 5" />
    </svg>
  );
}

function PremiumKpi({ label, value, change, icon, iconTone, loading }) {
  const { text, up } = change;
  return (
    <article className="premium-kpi">
      <div className="premium-kpi__head">
        <div className="premium-kpi__label">{label}</div>
        <div className={`premium-kpi__icon premium-kpi__icon--${iconTone || "green"}`}>
          {icon}
        </div>
      </div>
      {loading ? (
        <Skeleton width="55%" height={34} sx={{ mt: 0.35 }} />
      ) : (
        <div className="premium-kpi__value">{value}</div>
      )}
      {!loading && (
        <span className={`premium-kpi__change ${up ? "premium-kpi__change--up" : "premium-kpi__change--down"}`}>{text}</span>
      )}
    </article>
  );
}

function InsightCard({ title, value, actionLabel, actionTo, loading }) {
  return (
    <article className="dashboard-insight-card">
      <div className="dashboard-insight-card__title">{title}</div>
      {loading ? (
        <Skeleton width="30%" height={40} />
      ) : (
        <div className="dashboard-insight-card__value">{value}</div>
      )}
      <Link to={actionTo} className="dashboard-insight-card__link">
        {actionLabel}
      </Link>
    </article>
  );
}

function buildLast7DaysRevenue(orders) {
  const days = [];
  const now = new Date();
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    d.setHours(0, 0, 0, 0);
    days.push(d);
  }
  return days.map((day) => {
    const next = new Date(day);
    next.setDate(next.getDate() + 1);
    const total = orders.reduce((sum, o) => {
      const od = o.date ? new Date(o.date) : null;
      if (!od || Number.isNaN(od.getTime())) return sum;
      if (od >= day && od < next) return sum + Number(o.grandTotal || 0);
      return sum;
    }, 0);
    return {
      label: day.toLocaleDateString(undefined, { weekday: "short" }),
      revenue: Math.round(total * 100) / 100
    };
  });
}

export function DashboardPage() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [kpis, setKpis] = useState(null);
  const [tierData, setTierData] = useState([]);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const previousRef = useRef(null);

  const loadLiveData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError("");

    const [productsRes, ordersRes, customersRes, orderDetailsRes] = await Promise.all([
      apiGet("/api/Products"),
      apiGet("/api/Orders"),
      apiGet("/api/Customers"),
      apiGet("/api/OrderDetails")
    ]);

    if (!productsRes.ok || !ordersRes.ok || !customersRes.ok || !orderDetailsRes.ok) {
      setError(
        productsRes.error || ordersRes.error || customersRes.error || orderDetailsRes.error || "Failed to load dashboard."
      );
      setLoading(false);
      setRefreshing(false);
      return;
    }

    const products = productsRes.data ?? [];
    const orders = ordersRes.data ?? [];
    const customers = customersRes.data ?? [];
    const orderDetails = orderDetailsRes.data ?? [];

    const now = new Date();
    const month = now.getMonth();
    const year = now.getFullYear();

    const totalOrders = orders.length;
    const activeOrders = orders.filter((x) => !String(x.paymentStatus ?? "").toLowerCase().includes("cancel")).length;
    const monthlyRevenue = orders.reduce((sum, x) => {
      const d = x.date ? new Date(x.date) : null;
      if (d && !Number.isNaN(d.getTime()) && d.getMonth() === month && d.getFullYear() === year) {
        return sum + Number(x.grandTotal || 0);
      }
      return sum;
    }, 0);
    const monthlyCount = orders.filter((x) => {
      const d = x.date ? new Date(x.date) : null;
      return d && !Number.isNaN(d.getTime()) && d.getMonth() === month && d.getFullYear() === year;
    }).length;
    const avgMtd = monthlyCount > 0 ? monthlyRevenue / monthlyCount : 0;

    const cur = {
      products: products.length,
      customers: customers.length,
      totalOrders,
      activeOrders,
      monthlyRevenue,
      avgMtd
    };
    const prev = previousRef.current;

    setKpis({
      products: { value: String(cur.products), change: formatChange(cur.products, prev?.products) },
      customers: { value: String(cur.customers), change: formatChange(cur.customers, prev?.customers) },
      totalOrders: { value: String(cur.totalOrders), change: formatChange(cur.totalOrders, prev?.totalOrders) },
      activeOrders: { value: String(cur.activeOrders), change: formatChange(cur.activeOrders, prev?.activeOrders) },
      revenue: {
        value: `₹${cur.monthlyRevenue.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        change: formatChange(cur.monthlyRevenue, prev?.monthlyRevenue)
      },
      avgMtd: {
        value: `₹${cur.avgMtd.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        change: formatChange(cur.avgMtd, prev?.avgMtd)
      }
    });

    setRevenueTrend(buildLast7DaysRevenue(orders));

    const tierGroups = orders.reduce((acc, x) => {
      const a = Number(x.grandTotal || 0);
      let key = "Under ₹10,000";
      if (a >= 50000) key = "₹50,000+";
      else if (a >= 25000) key = "₹25,000–49,999";
      else if (a >= 10000) key = "₹10,000–24,999";
      acc[key] = (acc[key] ?? 0) + 1;
      return acc;
    }, {});
    setTierData(
      Object.entries(tierGroups)
        .sort((a, b) => b[1] - a[1])
        .map(([label, count], i) => ({
          id: i,
          label,
          value: count,
          color: TIER_COLORS[i % TIER_COLORS.length]
        }))
    );

    setStatusData(
      Object.entries(
        orders.reduce((acc, x) => {
          const k = (x.paymentStatus && String(x.paymentStatus).trim()) || "Unknown";
          acc[k] = (acc[k] ?? 0) + 1;
          return acc;
        }, {})
      )
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6)
    );

    setRecentOrders(
      [...orders]
        .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
        .slice(0, 8)
        .map((o) => {
          const c = customers.find((x) => x.id === o.customerId);
          return {
            id: o.id,
            orderNo: o.orderNo || `#${o.id}`,
            customerName: c?.name || `Customer #${o.customerId}`,
            amount: Number(o.grandTotal || 0),
            status: o.paymentStatus || "Unknown",
            date: o.date ? new Date(o.date).toLocaleDateString() : "—"
          };
        })
    );

    const qtyMap = orderDetails.reduce((acc, x) => {
      acc[x.productId] = (acc[x.productId] ?? 0) + 1;
      return acc;
    }, {});
    const maxQty = Math.max(1, ...Object.values(qtyMap));
    setTopProducts(
      Object.entries(qtyMap)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6)
        .map(([id, qty], i) => ({
          productId: Number(id),
          rank: i + 1,
          name: products.find((p) => p.id === Number(id))?.name || `Product #${id}`,
          qty,
          pct: (qty / maxQty) * 100
        }))
    );

    previousRef.current = cur;
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    void loadLiveData(false);
    const t = window.setInterval(() => void loadLiveData(true), 20000);
    return () => window.clearInterval(t);
  }, [loadLiveData]);

  const orderColumns = useMemo(
    () => [
      { field: "orderNo", headerName: "Order", flex: 0.9, minWidth: 90 },
      { field: "customerName", headerName: "Customer", flex: 1.1, minWidth: 110 },
      { field: "date", headerName: "Date", width: 100 },
      {
        field: "amount",
        headerName: "Amount",
        width: 100,
        renderCell: (p) => (
          <span style={{ fontWeight: 600, color: "#14532d" }}>${Number(p.value).toFixed(2)}</span>
        )
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.85,
        minWidth: 90,
        renderCell: (p) => {
          const paid = String(p.value).toLowerCase().includes("paid");
          return (
            <span
              style={{
                fontSize: "0.75rem",
                fontWeight: 600,
                padding: "0.2rem 0.5rem",
                borderRadius: 6,
                background: paid ? "#ecfdf5" : "#f1f5f9",
                color: paid ? "#047857" : "#64748b"
              }}
            >
              {p.value}
            </span>
          );
        }
      }
    ],
    []
  );

  return (
    <div className="premium-dashboard">
      <header className="dashboard-page-header">
        <div>
          <h1 className="dashboard-page-header__title">Dashboard</h1>
          <p className="dashboard-page-header__subtitle">
            Live snapshot of your property performance.
          </p>
        </div>
        <div className="dashboard-page-header__actions">
          <Button
            variant="outlined"
            className="dashboard-btn dashboard-btn--ghost"
            disabled={loading || refreshing}
            onClick={() => void loadLiveData(true)}
          >
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
          <Button component={Link} to="/orders-list" className="dashboard-btn dashboard-btn--primary">
            View Orders
          </Button>
        </div>
      </header>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <div className="premium-kpi-grid premium-kpi-grid--main">
        <PremiumKpi
          label="Products"
          value={kpis?.products.value ?? "—"}
          change={kpis?.products.change ?? { text: "—", up: true }}
          icon={<KpiIconProducts />}
          iconTone="green"
          loading={loading}
        />
        <PremiumKpi
          label="Revenue (MTD)"
          value={kpis?.revenue.value ?? "—"}
          change={kpis?.revenue.change ?? { text: "—", up: true }}
          icon={<KpiIconRevenue />}
          iconTone="gold"
          loading={loading}
        />
        <PremiumKpi
          label="Total orders"
          value={kpis?.totalOrders.value ?? "—"}
          change={kpis?.totalOrders.change ?? { text: "—", up: true }}
          icon={<KpiIconOrders />}
          iconTone="mint"
          loading={loading}
        />
        <PremiumKpi
          label="Customers"
          value={kpis?.customers.value ?? "—"}
          change={kpis?.customers.change ?? { text: "—", up: true }}
          icon={<KpiIconCustomers />}
          iconTone="forest"
          loading={loading}
        />
      </div>

      <div className="dashboard-insight-grid">
        <InsightCard
          title="Active orders"
          value={kpis?.activeOrders.value ?? "—"}
          actionLabel="Manage orders →"
          actionTo="/orders-list"
          loading={loading}
        />
        <InsightCard
          title="Avg order (MTD)"
          value={kpis?.avgMtd.value ?? "—"}
          actionLabel="View reports →"
          actionTo="/orders-list"
          loading={loading}
        />
        <InsightCard
          title="Catalog items"
          value={kpis?.products.value ?? "—"}
          actionLabel="View products →"
          actionTo="/products-list"
          loading={loading}
        />
      </div>

      <div className="premium-charts">
        <div className="premium-panel">
          <div className="premium-panel__header">
            <h2 className="premium-panel__title">Revenue trend</h2>
            <p className="premium-panel__desc">Last 7 days</p>
          </div>
          <div className="premium-panel__body">
            {loading ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress sx={{ color: CHART_GREEN }} />
              </Box>
            ) : (
              <LineChart
                xAxis={[{ scaleType: "point", data: revenueTrend.map((d) => d.label) }]}
                series={[
                  {
                    data: revenueTrend.map((d) => d.revenue),
                    color: CHART_GREEN,
                    curve: "natural",
                    showMark: true,
                    area: true
                  }
                ]}
                height={280}
                margin={{ top: 16, right: 24, bottom: 32, left: 56 }}
                grid={{ vertical: true, horizontal: true }}
                sx={{ "& .MuiLineElement-root": { strokeWidth: 2.5 } }}
              />
            )}
          </div>
        </div>

        <div className="premium-panel">
          <div className="premium-panel__header">
            <h2 className="premium-panel__title">Order tiers</h2>
            <p className="premium-panel__desc">By order value</p>
          </div>
          <div className="premium-panel__body">
            {loading ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress sx={{ color: CHART_GOLD }} />
              </Box>
            ) : (
              <Box display="flex" justifyContent="center">
                <PieChart
                  series={[
                    {
                      data:
                        tierData.length > 0
                          ? tierData.map((d) => ({ id: d.id, value: d.value, label: d.label, color: d.color }))
                          : [{ id: 0, value: 1, label: "No data", color: "#e2e8f0" }],
                      innerRadius: 58,
                      outerRadius: 100,
                      paddingAngle: 3,
                      cornerRadius: 6,
                      highlightScope: { fade: "global", highlight: "item" }
                    }
                  ]}
                  width={300}
                  height={280}
                  margin={{ right: 5 }}
                />
              </Box>
            )}
          </div>
        </div>
      </div>

      <div className="premium-bottom">
        <div className="premium-panel">
          <div className="premium-panel__header">
            <h2 className="premium-panel__title">Recent orders</h2>
            <p className="premium-panel__desc">Latest transactions</p>
          </div>
          <div className="premium-panel__body premium-grid-shell">
            <DataGrid
              rows={recentOrders}
              columns={orderColumns}
              loading={loading}
              disableRowSelectionOnClick
              hideFooter
              autoHeight
              disableColumnMenu
              sx={gridSx}
            />
          </div>
        </div>

        <div className="premium-panel">
          <div className="premium-panel__header">
            <h2 className="premium-panel__title">Top products</h2>
            <p className="premium-panel__desc">By units sold</p>
          </div>
          <div className="premium-panel__body">
            {loading ? (
              <Stack spacing={1}>
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} height={52} sx={{ borderRadius: 2 }} />
                ))}
              </Stack>
            ) : topProducts.length === 0 ? (
              <p style={{ color: "#64748b", margin: 0, fontSize: "0.875rem" }}>No sales data yet.</p>
            ) : (
              topProducts.map((item) => (
                <div key={item.productId} className="premium-rank">
                  <div className={`premium-rank__badge${item.rank > 3 ? " premium-rank__badge--alt" : ""}`}>{item.rank}</div>
                  <span className="premium-rank__name" title={item.name}>
                    {item.name}
                  </span>
                  <div className="premium-rank__bar-wrap">
                    <div className="premium-rank__bar" style={{ width: `${item.pct}%` }} />
                  </div>
                  <span className="premium-rank__qty">{item.qty} sold</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {statusData.length > 0 && (
        <div className="premium-panel">
          <div className="premium-panel__header">
            <h2 className="premium-panel__title">Payment status</h2>
            <p className="premium-panel__desc">Order distribution</p>
          </div>
          <div className="premium-panel__body">
            {loading ? (
              <CircularProgress size={32} sx={{ color: CHART_GREEN }} />
            ) : (
              <BarChart
                xAxis={[{ scaleType: "band", data: statusData.map(([l]) => l) }]}
                series={[{ data: statusData.map(([, c]) => c), color: CHART_GREEN }]}
                height={220}
                margin={{ left: 48, right: 16, top: 12, bottom: 48 }}
                borderRadius={8}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
